import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import './AgentLea.css';

import { AgentHeader } from './components/AgentHeader/AgentHeader';
import { AgentSubNav } from './components/AgentSubNav/AgentSubNav';
import { PlansView } from './components/PlansView/PlansView';
import { HistoryView } from './components/HistoryView/HistoryView';
import { ControlView } from './components/ControlView/ControlView';
import { DecisionsView } from './components/DecisionsView/DecisionsView';

import { useAgentStatus } from './hooks/useAgentStatus';
import { useAgentStats } from './hooks/useAgentStats';
import { agentAPI } from './api/agent.api';

import type { AgentTab, PlanFiltersState } from './types/agent.types';

const DEFAULT_FILTERS: PlanFiltersState = {
  role: 'all',
  planType: null,
  priority: null,
  search: '',
  statusFilter: null,
};

export const AgentLeaTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AgentTab>(() => {
    const saved = sessionStorage.getItem('agentLeaActiveTab') as AgentTab | null;
    if (saved && ['plans', 'control', 'decisions', 'history'].includes(saved)) {
      return saved;
    }
    return 'plans';
  });

  const handleTabChange = (tab: AgentTab) => {
    setActiveTab(tab);
    sessionStorage.setItem('agentLeaActiveTab', tab);
  };

  const [filters, setFilters] = useState<PlanFiltersState>(DEFAULT_FILTERS);

  const { status } = useAgentStatus();
  const { stats } = useAgentStats();

  const { data: pendingCountData } = useQuery({
    queryKey: ['agent-pending-count'],
    queryFn: () => agentAPI.getPendingCount(),
    staleTime: 10000,
    gcTime: 30000,
  });

  const totalPending = (pendingCountData?.data?.secretariat_count ?? 0) + (pendingCountData?.data?.fch_count ?? 0);

  const handleFiltersChange = useCallback((partial: Partial<PlanFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const renderActiveView = () => {
    switch (activeTab) {
      case 'plans':
        return (
          <PlansView
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        );
      case 'control':
        return <ControlView />;
      case 'decisions':
        return <DecisionsView />;
      case 'history':
        return (
          <HistoryView
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="lea-page">
      <AgentHeader
        status={status}
        stats={stats}
        pendingCount={totalPending}
      />

      <div className="lea-content">
        <AgentSubNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          pendingCount={totalPending}
        />

        <div className="lea-view">
          {renderActiveView()}
        </div>
      </div>
    </div>
  );
};
